import * as React from "react";
import styled from "@emotion/styled";
import { UseFormRegisterReturn } from "react-hook-form";
import { Button, FieldError, LoadingSpinner, Text } from "atoms";
import { useImageUpload } from "../api/useUploadImage";
import { UploadedImage } from "../types";
import { ImageCropper, HiddenInput } from "./ImageCropper";
import { Thumbnail } from "./Thumbnail";
import { formatSize } from "../utils/formatSize";

type ImageUploadProps = {
  errorMessage?: string;
  fieldRegister: UseFormRegisterReturn;
  onSuccess: (upload: UploadedImage) => void;
  src?: string;
  entityType: "ARTIST" | "PROJECT";
  small?: boolean;
  maxFileSize?: number;
  banner?: boolean;
};

function ImageUpload({
  onSuccess,
  fieldRegister,
  errorMessage,
  src,
  entityType,
  small = false,
  maxFileSize,
  banner,
}: ImageUploadProps) {
  const [isUploading, setIsUploading] = React.useState(!src);
  const toggleIsUploading = () => {
    setIsUploading(!isUploading);
  };

  const { runUpload, upload, isLoading, isSuccess, error } = useImageUpload();

  let previewSrc = src;
  let onPreviewLoad = () => {};
  if (upload) {
    previewSrc = URL.createObjectURL(upload.file);
    onPreviewLoad = () => URL.revokeObjectURL(previewSrc as string);
  }

  // When upload is successful, update the parent component (e.g. set form field values)
  React.useEffect(() => {
    if (isSuccess && upload) {
      onSuccess(upload);
      setIsUploading(false);
    }
  }, [onSuccess, isSuccess, upload]);

  // When user completes crop action and submits file, attempt to upload it
  const onCropSubmit = (srcFile: File, cropBlob: Blob) => {
    const { name, type } = srcFile as File;
    const newFile = new File([cropBlob], name, { type });
    runUpload(newFile);
  };

  return (
    <>
      {isUploading ? (
        <ImageCropper
          onSubmit={onCropSubmit}
          onCancel={src ? toggleIsUploading : undefined}
          small={small}
          maxFileSize={maxFileSize}
          banner={banner}
        />
      ) : (
        <UploadedPreview>
          {previewSrc && (
            <figure>
              <SThumbnail
                src={previewSrc}
                alt="Upload preview"
                onLoad={onPreviewLoad}
                rounded={entityType === "PROJECT"}
                circle={entityType === "ARTIST"}
                entityType={entityType}
              />
              <Text as="figcaption" size="xxs">
                <em>Preview</em>
              </Text>
            </figure>
          )}

          <UploadedPreviewActions>
            {upload && (
              <Text size="xs">
                {upload.file.name} — {formatSize(upload.file.size)}
              </Text>
            )}
            <Button onClick={toggleIsUploading} size="xs">
              Replace Image
            </Button>
          </UploadedPreviewActions>
        </UploadedPreview>
      )}

      {isLoading && <LoadingSpinner />}

      <HiddenInput type="text" readOnly {...fieldRegister} />

      {(errorMessage || error) && (
        <FieldError>
          {errorMessage ?? "Sorry, the upload failed. Try again."}
        </FieldError>
      )}
    </>
  );
}

const flexStyles = {
  alignItems: "start",
  display: "flex",
  gap: "2rem",
  justifyContent: "start",
} as const;

const UploadedPreview = styled.div({
  ...flexStyles,
  flexDirection: "row",
  alignItems: "center",
  figure: {
    margin: 0,
    textAlign: "center",
  },
});

const SThumbnail = styled(Thumbnail)<{ entityType?: string }>(
  ({ entityType }) => ({
    width: 200,
    ...(entityType && {
      width: 80,
    }),
  })
);
const UploadedPreviewActions = styled.div({
  // ...flexStyles,
  // flexDirection: "column",
  // gap: "1rem",
});

export { ImageUpload };
